import * as React from "react";
import { Link } from "react-router";
import type { VerknuepftesTodoDto } from "../../../services/vorgangService";
import { twvDateView } from "../detail/twvFrist";
import { istTodoErledigt } from "../detail/todoRowActions";
import { IcoCheck, IcoX } from "./vg3Icons";
import "./vorgangV3.css";

/**
 * Ein To-do, das einem ANDEREN Vorgang gehört und hier mit angezeigt wird
 * (PO 05.09.2026: „Todo eines anderen auswählen … in beiden Vorgängen zeigen").
 *
 * Bewusst eine schlanke Karte statt eines vollen `TodoBlock`: der Verlauf des
 * To-dos hängt an seinem Heimat-Vorgang, und diese Seite lädt ausschließlich
 * die vier Quellen des EIGENEN Vorgangs. Ein aufklappbarer Block zeigte hier
 * also eine leere Zeitachse und behauptete damit, es sei nichts passiert.
 *
 * Aus demselben Grund ist der Status hier nur zu LESEN: das To-do hat genau
 * einen Vorgang, dem es gehört, und dort wird es auch erledigt. Der Verweis
 * führt direkt dorthin.
 */
type Props = {
    todo: VerknuepftesTodoDto;
    canEdit: boolean;
    onLoesen: (todo: VerknuepftesTodoDto) => void;
    /**
     * Wohin der „Gehört zu"-Verweis führt — ohne die Id, z. B. `/vorgaenge`.
     *
     * Der Vorgabewert ist `/vorgaenge` — die eine Vorgangsansicht. Bis zum
     * 20.09.2026 stand hier `/vorgaenge/v3`, weil die Karte aus V3 stammt und
     * V3, V5 und V6 sie mitbenutzten; diese Ansichten gibt es nicht mehr.
     */
    zielBasis?: string;
};

export const VerknuepftesTodoKarte: React.FC<Props> = ({ todo, canEdit, onLoesen, zielBasis = "/vorgaenge" }) => {
    const done = istTodoErledigt(todo.status);
    const datum = (wert: string | null): string => twvDateView(wert)?.date ?? "—";

    return (
        <div className={`vg3-vtodo${done ? " done" : ""}`}>
            <div className="vg3-vtodo-z">
                <span className={`vg3-check${done ? " done" : ""}`} aria-hidden>
                    <IcoCheck size={14} />
                </span>
                <span className="vg3-vtodo-t">{todo.titel || "(ohne Betreff)"}</span>
                <span className="vg3-badge" title="Status im Heimat-Vorgang">
                    {todo.status || "Offen"}
                </span>

                <span className="vg3-vtodo-meta">
                    <span className="vg3-todo-kopf-frist">
                        TWV: {datum(todo.twv)} | Frist: {datum(todo.frist)}
                    </span>
                    {canEdit && (
                        <button
                            type="button"
                            className="vg3-ibtn"
                            title="Verknüpfung lösen — das To-do selbst bleibt"
                            aria-label="Verknüpfung lösen"
                            onClick={() => onLoesen(todo)}
                        >
                            <IcoX size={15} />
                        </button>
                    )}
                </span>
            </div>

            <div className="vg3-vtodo-herkunft">
                <span>Gehört zu</span>
                <Link to={`${zielBasis}/${todo.vorgangId}`} className="vg3-vtodo-link">
                    {todo.vorgangNbr ? `${todo.vorgangNbr} · ` : ""}
                    {todo.vorgangTitel || `Vorgang ${todo.vorgangId}`}
                </Link>
                <span className="vg3-leer">— Verlauf und Erledigen stehen dort</span>
            </div>
        </div>
    );
};
